/**
 * Bundled ticker dataset for the autocomplete dropdown on the Stocks &
 * ETFs and Metals sub-cards (Patch BJ).
 *
 * Curated to be 100% resolvable on Yahoo Finance (Paul's requirement: no
 * dropdown entry should error out when the user tries to add it). The
 * client-side autocomplete filters this list as the user types; on commit
 * the price-lookup proxy (/api/etf-price) is called to validate and pull
 * the live price. If a user needs a ticker that's not in this list, the
 * "Custom..." option opens a free-text field that hits the same proxy.
 *
 * Coverage rationale: the list is intentionally curated, not exhaustive,
 * because the marginal value of adding small-cap names beyond a few
 * hundred is low for cohort-scale retirement planning. The ~200 entries
 * below capture:
 *   - Top market-cap S&P 500 names (the ones cohort users actually hold)
 *   - All major broad-market ETFs (SPY/VOO/VTI/QQQ families)
 *   - Top Vanguard / Fidelity / Schwab mutual funds (the 401(k) menu)
 *   - Bond ETFs (AGG/BND/TLT etc.)
 *   - Commodity ETFs (DBC/PDBC/USO)
 *   - Crypto-adjacent ETFs and stocks (IBIT/FBTC/MSTR/COIN)
 *   - Major metal ETFs (GLD/SLV/IAU et al)
 *   - Spot metal pseudo-tickers (XAUUSD=X for gold, etc.)
 *
 * Refresh cadence: annual. S&P 500 constituents change a few times a year;
 * ETF/MF list is stable. To expand toward the full S&P 1500, append rows
 * with kind: 'stock' from the S&P MidCap 400 and SmallCap 600 lists
 * (Wikipedia maintains current constituents).
 *
 * Bundle size: roughly 25KB uncompressed, ~8KB gzipped. Trivial bundle
 * impact even at 5x this size.
 */

import type { SecurityAssetClass } from '../engine/types.js';

export interface BundledTicker {
  /** Yahoo Finance symbol. Must resolve via /api/etf-price for the
   *  full add-to-portfolio flow to succeed. */
  symbol: string;
  /** Human-readable name shown in the autocomplete dropdown. */
  name: string;
  /** Routes to the appropriate sub-card (stocks/ETFs vs metals) and
   *  drives the pie-chart color theme on the Dashboard. */
  kind: SecurityAssetClass;
}

export const BUNDLED_TICKERS: BundledTicker[] = [
  // ---- Spot metals (Yahoo's FX-style pseudo-tickers) ----
  // These return USD per troy ounce; the security card converts oz <-> grams
  // for the metals display unit. Stored quantity is in grams.
  { symbol: 'XAUUSD=X', name: 'Gold spot (USD/oz)', kind: 'spot_metal' },
  { symbol: 'XAGUSD=X', name: 'Silver spot (USD/oz)', kind: 'spot_metal' },
  { symbol: 'XPTUSD=X', name: 'Platinum spot (USD/oz)', kind: 'spot_metal' },
  { symbol: 'XPDUSD=X', name: 'Palladium spot (USD/oz)', kind: 'spot_metal' },

  // ---- Major metal ETFs ----
  { symbol: 'GLD', name: 'SPDR Gold Shares', kind: 'metal_etf' },
  { symbol: 'IAU', name: 'iShares Gold Trust', kind: 'metal_etf' },
  { symbol: 'GLDM', name: 'SPDR Gold MiniShares', kind: 'metal_etf' },
  { symbol: 'SGOL', name: 'abrdn Physical Gold Shares ETF', kind: 'metal_etf' },
  { symbol: 'BAR', name: 'GraniteShares Gold Trust', kind: 'metal_etf' },
  { symbol: 'SLV', name: 'iShares Silver Trust', kind: 'metal_etf' },
  { symbol: 'SIVR', name: 'abrdn Physical Silver Shares ETF', kind: 'metal_etf' },
  { symbol: 'PSLV', name: 'Sprott Physical Silver Trust', kind: 'metal_etf' },
  { symbol: 'PPLT', name: 'abrdn Physical Platinum Shares ETF', kind: 'metal_etf' },
  { symbol: 'PALL', name: 'abrdn Physical Palladium Shares ETF', kind: 'metal_etf' },

  // ---- Broad-market ETFs ----
  { symbol: 'SPY', name: 'SPDR S&P 500 ETF Trust', kind: 'etf' },
  { symbol: 'VOO', name: 'Vanguard S&P 500 ETF', kind: 'etf' },
  { symbol: 'IVV', name: 'iShares Core S&P 500 ETF', kind: 'etf' },
  { symbol: 'VTI', name: 'Vanguard Total Stock Market ETF', kind: 'etf' },
  { symbol: 'ITOT', name: 'iShares Core S&P Total US Stock ETF', kind: 'etf' },
  { symbol: 'SCHB', name: 'Schwab US Broad Market ETF', kind: 'etf' },
  { symbol: 'QQQ', name: 'Invesco QQQ Trust', kind: 'etf' },
  { symbol: 'QQQM', name: 'Invesco NASDAQ 100 ETF', kind: 'etf' },
  { symbol: 'DIA', name: 'SPDR Dow Jones Industrial Average ETF', kind: 'etf' },
  { symbol: 'IWM', name: 'iShares Russell 2000 ETF', kind: 'etf' },
  { symbol: 'IJR', name: 'iShares Core S&P Small-Cap ETF', kind: 'etf' },
  { symbol: 'IJH', name: 'iShares Core S&P Mid-Cap ETF', kind: 'etf' },
  { symbol: 'VO', name: 'Vanguard Mid-Cap ETF', kind: 'etf' },
  { symbol: 'VB', name: 'Vanguard Small-Cap ETF', kind: 'etf' },

  // ---- International ETFs ----
  { symbol: 'VXUS', name: 'Vanguard Total International Stock ETF', kind: 'etf' },
  { symbol: 'VEA', name: 'Vanguard FTSE Developed Markets ETF', kind: 'etf' },
  { symbol: 'VWO', name: 'Vanguard FTSE Emerging Markets ETF', kind: 'etf' },
  { symbol: 'EFA', name: 'iShares MSCI EAFE ETF', kind: 'etf' },
  { symbol: 'IEMG', name: 'iShares Core MSCI Emerging Markets ETF', kind: 'etf' },
  { symbol: 'IXUS', name: 'iShares Core MSCI Total International ETF', kind: 'etf' },
  { symbol: 'EEM', name: 'iShares MSCI Emerging Markets ETF', kind: 'etf' },

  // ---- Carry-strategy income ETFs ----
  { symbol: 'STRC', name: 'STRC Option Income Strategy ETF', kind: 'etf' },

  // ---- Dividend / factor / sector ETFs ----
  { symbol: 'VYM', name: 'Vanguard High Dividend Yield ETF', kind: 'etf' },
  { symbol: 'SCHD', name: 'Schwab US Dividend Equity ETF', kind: 'etf' },
  { symbol: 'DGRO', name: 'iShares Core Dividend Growth ETF', kind: 'etf' },
  { symbol: 'NOBL', name: 'ProShares S&P 500 Dividend Aristocrats ETF', kind: 'etf' },
  { symbol: 'MOAT', name: 'VanEck Morningstar Wide Moat ETF', kind: 'etf' },
  { symbol: 'XLK', name: 'Technology Select Sector SPDR Fund', kind: 'etf' },
  { symbol: 'XLF', name: 'Financial Select Sector SPDR Fund', kind: 'etf' },
  { symbol: 'XLE', name: 'Energy Select Sector SPDR Fund', kind: 'etf' },
  { symbol: 'XLV', name: 'Health Care Select Sector SPDR Fund', kind: 'etf' },
  { symbol: 'XLY', name: 'Consumer Discretionary Select Sector SPDR Fund', kind: 'etf' },
  { symbol: 'XLP', name: 'Consumer Staples Select Sector SPDR Fund', kind: 'etf' },
  { symbol: 'XLI', name: 'Industrial Select Sector SPDR Fund', kind: 'etf' },
  { symbol: 'XLU', name: 'Utilities Select Sector SPDR Fund', kind: 'etf' },
  { symbol: 'XLB', name: 'Materials Select Sector SPDR Fund', kind: 'etf' },
  { symbol: 'XLRE', name: 'Real Estate Select Sector SPDR Fund', kind: 'etf' },
  { symbol: 'XLC', name: 'Communication Services Select Sector SPDR Fund', kind: 'etf' },

  // ---- Bond ETFs ----
  { symbol: 'AGG', name: 'iShares Core US Aggregate Bond ETF', kind: 'bond_etf' },
  { symbol: 'BND', name: 'Vanguard Total Bond Market ETF', kind: 'bond_etf' },
  { symbol: 'BNDX', name: 'Vanguard Total International Bond ETF', kind: 'bond_etf' },
  { symbol: 'BSV', name: 'Vanguard Short-Term Bond ETF', kind: 'bond_etf' },
  { symbol: 'BIV', name: 'Vanguard Intermediate-Term Bond ETF', kind: 'bond_etf' },
  { symbol: 'BLV', name: 'Vanguard Long-Term Bond ETF', kind: 'bond_etf' },
  { symbol: 'TLT', name: 'iShares 20+ Year Treasury Bond ETF', kind: 'bond_etf' },
  { symbol: 'IEF', name: 'iShares 7-10 Year Treasury Bond ETF', kind: 'bond_etf' },
  { symbol: 'SHY', name: 'iShares 1-3 Year Treasury Bond ETF', kind: 'bond_etf' },
  { symbol: 'TIP', name: 'iShares TIPS Bond ETF', kind: 'bond_etf' },
  { symbol: 'VTIP', name: 'Vanguard Short-Term TIPS ETF', kind: 'bond_etf' },
  { symbol: 'LQD', name: 'iShares iBoxx $ Investment Grade Corporate Bond ETF', kind: 'bond_etf' },
  { symbol: 'HYG', name: 'iShares iBoxx $ High Yield Corporate Bond ETF', kind: 'bond_etf' },
  { symbol: 'MUB', name: 'iShares National Muni Bond ETF', kind: 'bond_etf' },
  { symbol: 'EMB', name: 'iShares JP Morgan USD Emerging Markets Bond ETF', kind: 'bond_etf' },

  // ---- Commodity ETFs (non-metal) ----
  { symbol: 'DBC', name: 'Invesco DB Commodity Index Tracking Fund', kind: 'commodity_etf' },
  { symbol: 'PDBC', name: 'Invesco Optimum Yield Diversified Commodity Strategy', kind: 'commodity_etf' },
  { symbol: 'DBA', name: 'Invesco DB Agriculture Fund', kind: 'commodity_etf' },
  { symbol: 'USO', name: 'United States Oil Fund', kind: 'commodity_etf' },
  { symbol: 'UNG', name: 'United States Natural Gas Fund', kind: 'commodity_etf' },
  { symbol: 'CPER', name: 'United States Copper Index Fund', kind: 'commodity_etf' },
  { symbol: 'CORN', name: 'Teucrium Corn Fund', kind: 'commodity_etf' },
  { symbol: 'WEAT', name: 'Teucrium Wheat Fund', kind: 'commodity_etf' },

  // ---- Crypto ETFs and adjacent ----
  { symbol: 'IBIT', name: 'iShares Bitcoin Trust', kind: 'crypto_etf' },
  { symbol: 'FBTC', name: 'Fidelity Wise Origin Bitcoin Fund', kind: 'crypto_etf' },
  { symbol: 'BITB', name: 'Bitwise Bitcoin ETF Trust', kind: 'crypto_etf' },
  { symbol: 'ARKB', name: 'ARK 21Shares Bitcoin ETF', kind: 'crypto_etf' },
  { symbol: 'HODL', name: 'VanEck Bitcoin Trust', kind: 'crypto_etf' },
  { symbol: 'BTC', name: 'Grayscale Bitcoin Mini Trust', kind: 'crypto_etf' },
  { symbol: 'GBTC', name: 'Grayscale Bitcoin Trust ETF', kind: 'crypto_etf' },
  { symbol: 'ETHA', name: 'iShares Ethereum Trust', kind: 'crypto_etf' },
  { symbol: 'FETH', name: 'Fidelity Ethereum Fund', kind: 'crypto_etf' },
  { symbol: 'MSTR', name: 'Strategy (Bitcoin treasury)', kind: 'stock' },
  // COIN moved to S&P 500 section below (added to index 2025-05-19).
  { symbol: 'MARA', name: 'Marathon Digital Holdings', kind: 'stock' },
  { symbol: 'RIOT', name: 'Riot Platforms', kind: 'stock' },
  { symbol: 'CLSK', name: 'CleanSpark', kind: 'stock' },
  { symbol: 'HUT', name: 'Hut 8', kind: 'stock' },

  // ---- Vanguard mutual funds (the 401(k) menu staples) ----
  { symbol: 'VTSAX', name: 'Vanguard Total Stock Market Index Admiral', kind: 'mutual_fund' },
  { symbol: 'VFIAX', name: 'Vanguard 500 Index Admiral', kind: 'mutual_fund' },
  { symbol: 'VTIAX', name: 'Vanguard Total International Stock Index Admiral', kind: 'mutual_fund' },
  { symbol: 'VBTLX', name: 'Vanguard Total Bond Market Index Admiral', kind: 'mutual_fund' },
  { symbol: 'VEXAX', name: 'Vanguard Extended Market Index Admiral', kind: 'mutual_fund' },
  { symbol: 'VSMAX', name: 'Vanguard Small-Cap Index Admiral', kind: 'mutual_fund' },
  { symbol: 'VIMAX', name: 'Vanguard Mid-Cap Index Admiral', kind: 'mutual_fund' },
  { symbol: 'VGSLX', name: 'Vanguard Real Estate Index Admiral', kind: 'mutual_fund' },
  { symbol: 'VTSMX', name: 'Vanguard Total Stock Market Index Investor', kind: 'mutual_fund' },
  { symbol: 'VFINX', name: 'Vanguard 500 Index Investor', kind: 'mutual_fund' },
  { symbol: 'VBINX', name: 'Vanguard Balanced Index Investor', kind: 'mutual_fund' },
  { symbol: 'VWELX', name: 'Vanguard Wellington Investor', kind: 'mutual_fund' },
  { symbol: 'VWINX', name: 'Vanguard Wellesley Income Investor', kind: 'mutual_fund' },
  { symbol: 'VTHRX', name: 'Vanguard Target Retirement 2030', kind: 'mutual_fund' },
  { symbol: 'VFORX', name: 'Vanguard Target Retirement 2040', kind: 'mutual_fund' },
  { symbol: 'VFIFX', name: 'Vanguard Target Retirement 2050', kind: 'mutual_fund' },
  { symbol: 'VTTSX', name: 'Vanguard Target Retirement 2060', kind: 'mutual_fund' },

  // ---- Fidelity mutual funds ----
  { symbol: 'FXAIX', name: 'Fidelity 500 Index', kind: 'mutual_fund' },
  { symbol: 'FZROX', name: 'Fidelity ZERO Total Market Index', kind: 'mutual_fund' },
  { symbol: 'FSKAX', name: 'Fidelity Total Market Index', kind: 'mutual_fund' },
  { symbol: 'FZILX', name: 'Fidelity ZERO International Index', kind: 'mutual_fund' },
  { symbol: 'FTIHX', name: 'Fidelity Total International Index', kind: 'mutual_fund' },
  { symbol: 'FXNAX', name: 'Fidelity US Bond Index', kind: 'mutual_fund' },
  { symbol: 'FCNTX', name: 'Fidelity Contrafund', kind: 'mutual_fund' },
  { symbol: 'FBGRX', name: 'Fidelity Blue Chip Growth', kind: 'mutual_fund' },
  { symbol: 'FMAGX', name: 'Fidelity Magellan', kind: 'mutual_fund' },

  // ---- Schwab mutual funds ----
  { symbol: 'SWPPX', name: 'Schwab S&P 500 Index', kind: 'mutual_fund' },
  { symbol: 'SWTSX', name: 'Schwab Total Stock Market Index', kind: 'mutual_fund' },
  { symbol: 'SWAGX', name: 'Schwab US Aggregate Bond Index', kind: 'mutual_fund' },
  { symbol: 'SWISX', name: 'Schwab International Index', kind: 'mutual_fund' },

  // ---- S&P 500 (full list, Patch BM) ----
  // 503 entries sourced from github.com/datasets/s-and-p-500-companies
  // (constituents.csv on the main branch, kept up to date from
  // Wikipedia). Refresh annually with a fresh fetch + rerun of the
  // generator. Dotted symbols (BRK.B, BF.B) converted to Yahoo's
  // hyphenated form (BRK-B, BF-B) since that's what /api/etf-price
  // resolves through. Every entry hand-verified to round-trip through
  // Yahoo's quote endpoint at compile time of this patch.
  { symbol: 'MMM', name: '3M', kind: 'stock' },
  { symbol: 'AOS', name: 'A. O. Smith', kind: 'stock' },
  { symbol: 'ABT', name: 'Abbott Laboratories', kind: 'stock' },
  { symbol: 'ABBV', name: 'AbbVie', kind: 'stock' },
  { symbol: 'ACN', name: 'Accenture', kind: 'stock' },
  { symbol: 'ADBE', name: 'Adobe Inc.', kind: 'stock' },
  { symbol: 'AMD', name: 'Advanced Micro Devices', kind: 'stock' },
  { symbol: 'AES', name: 'AES Corporation', kind: 'stock' },
  { symbol: 'AFL', name: 'Aflac', kind: 'stock' },
  { symbol: 'A', name: 'Agilent Technologies', kind: 'stock' },
  { symbol: 'APD', name: 'Air Products', kind: 'stock' },
  { symbol: 'ABNB', name: 'Airbnb', kind: 'stock' },
  { symbol: 'AKAM', name: 'Akamai Technologies', kind: 'stock' },
  { symbol: 'ALB', name: 'Albemarle Corporation', kind: 'stock' },
  { symbol: 'ARE', name: 'Alexandria Real Estate Equities', kind: 'stock' },
  { symbol: 'ALGN', name: 'Align Technology', kind: 'stock' },
  { symbol: 'ALLE', name: 'Allegion', kind: 'stock' },
  { symbol: 'LNT', name: 'Alliant Energy', kind: 'stock' },
  { symbol: 'ALL', name: 'Allstate', kind: 'stock' },
  { symbol: 'GOOGL', name: 'Alphabet Inc. (Class A)', kind: 'stock' },
  { symbol: 'GOOG', name: 'Alphabet Inc. (Class C)', kind: 'stock' },
  { symbol: 'MO', name: 'Altria', kind: 'stock' },
  { symbol: 'AMZN', name: 'Amazon', kind: 'stock' },
  { symbol: 'AMCR', name: 'Amcor', kind: 'stock' },
  { symbol: 'AEE', name: 'Ameren', kind: 'stock' },
  { symbol: 'AEP', name: 'American Electric Power', kind: 'stock' },
  { symbol: 'AXP', name: 'American Express', kind: 'stock' },
  { symbol: 'AIG', name: 'American International Group', kind: 'stock' },
  { symbol: 'AMT', name: 'American Tower', kind: 'stock' },
  { symbol: 'AWK', name: 'American Water Works', kind: 'stock' },
  { symbol: 'AMP', name: 'Ameriprise Financial', kind: 'stock' },
  { symbol: 'AME', name: 'Ametek', kind: 'stock' },
  { symbol: 'AMGN', name: 'Amgen', kind: 'stock' },
  { symbol: 'APH', name: 'Amphenol', kind: 'stock' },
  { symbol: 'ADI', name: 'Analog Devices', kind: 'stock' },
  { symbol: 'AON', name: 'Aon plc', kind: 'stock' },
  { symbol: 'APA', name: 'APA Corporation', kind: 'stock' },
  { symbol: 'APO', name: 'Apollo Global Management', kind: 'stock' },
  { symbol: 'AAPL', name: 'Apple Inc.', kind: 'stock' },
  { symbol: 'AMAT', name: 'Applied Materials', kind: 'stock' },
  { symbol: 'APP', name: 'AppLovin', kind: 'stock' },
  { symbol: 'APTV', name: 'Aptiv', kind: 'stock' },
  { symbol: 'ACGL', name: 'Arch Capital Group', kind: 'stock' },
  { symbol: 'ADM', name: 'Archer Daniels Midland', kind: 'stock' },
  { symbol: 'ARES', name: 'Ares Management', kind: 'stock' },
  { symbol: 'ANET', name: 'Arista Networks', kind: 'stock' },
  { symbol: 'AJG', name: 'Arthur J. Gallagher & Co.', kind: 'stock' },
  { symbol: 'AIZ', name: 'Assurant', kind: 'stock' },
  { symbol: 'T', name: 'AT&T', kind: 'stock' },
  { symbol: 'ATO', name: 'Atmos Energy', kind: 'stock' },
  { symbol: 'ADSK', name: 'Autodesk', kind: 'stock' },
  { symbol: 'ADP', name: 'Automatic Data Processing', kind: 'stock' },
  { symbol: 'AZO', name: 'AutoZone', kind: 'stock' },
  { symbol: 'AVB', name: 'AvalonBay Communities', kind: 'stock' },
  { symbol: 'AVY', name: 'Avery Dennison', kind: 'stock' },
  { symbol: 'AXON', name: 'Axon Enterprise', kind: 'stock' },
  { symbol: 'BKR', name: 'Baker Hughes', kind: 'stock' },
  { symbol: 'BALL', name: 'Ball Corporation', kind: 'stock' },
  { symbol: 'BAC', name: 'Bank of America', kind: 'stock' },
  { symbol: 'BAX', name: 'Baxter International', kind: 'stock' },
  { symbol: 'BDX', name: 'Becton Dickinson', kind: 'stock' },
  { symbol: 'BRK-B', name: 'Berkshire Hathaway', kind: 'stock' },
  { symbol: 'BBY', name: 'Best Buy', kind: 'stock' },
  { symbol: 'TECH', name: 'Bio-Techne', kind: 'stock' },
  { symbol: 'BIIB', name: 'Biogen', kind: 'stock' },
  { symbol: 'BLK', name: 'BlackRock', kind: 'stock' },
  { symbol: 'BX', name: 'Blackstone Inc.', kind: 'stock' },
  { symbol: 'XYZ', name: 'Block, Inc.', kind: 'stock' },
  { symbol: 'BK', name: 'BNY Mellon', kind: 'stock' },
  { symbol: 'BA', name: 'Boeing', kind: 'stock' },
  { symbol: 'BKNG', name: 'Booking Holdings', kind: 'stock' },
  { symbol: 'BSX', name: 'Boston Scientific', kind: 'stock' },
  { symbol: 'BMY', name: 'Bristol Myers Squibb', kind: 'stock' },
  { symbol: 'AVGO', name: 'Broadcom', kind: 'stock' },
  { symbol: 'BR', name: 'Broadridge Financial Solutions', kind: 'stock' },
  { symbol: 'BRO', name: 'Brown & Brown', kind: 'stock' },
  { symbol: 'BF-B', name: 'Brown-Forman', kind: 'stock' },
  { symbol: 'BLDR', name: 'Builders FirstSource', kind: 'stock' },
  { symbol: 'BG', name: 'Bunge Global', kind: 'stock' },
  { symbol: 'BXP', name: 'BXP Inc.', kind: 'stock' },
  { symbol: 'CHRW', name: 'C.H. Robinson', kind: 'stock' },
  { symbol: 'CDNS', name: 'Cadence Design Systems', kind: 'stock' },
  { symbol: 'CPT', name: 'Camden Property Trust', kind: 'stock' },
  { symbol: 'CPB', name: "Campbell's Company", kind: 'stock' },
  { symbol: 'COF', name: 'Capital One', kind: 'stock' },
  { symbol: 'CAH', name: 'Cardinal Health', kind: 'stock' },
  { symbol: 'CCL', name: 'Carnival', kind: 'stock' },
  { symbol: 'CARR', name: 'Carrier Global', kind: 'stock' },
  { symbol: 'CVNA', name: 'Carvana', kind: 'stock' },
  { symbol: 'CASY', name: "Casey's", kind: 'stock' },
  { symbol: 'CAT', name: 'Caterpillar Inc.', kind: 'stock' },
  { symbol: 'CBOE', name: 'Cboe Global Markets', kind: 'stock' },
  { symbol: 'CBRE', name: 'CBRE Group', kind: 'stock' },
  { symbol: 'CDW', name: 'CDW Corporation', kind: 'stock' },
  { symbol: 'COR', name: 'Cencora', kind: 'stock' },
  { symbol: 'CNC', name: 'Centene Corporation', kind: 'stock' },
  { symbol: 'CNP', name: 'CenterPoint Energy', kind: 'stock' },
  { symbol: 'CF', name: 'CF Industries', kind: 'stock' },
  { symbol: 'CRL', name: 'Charles River Laboratories', kind: 'stock' },
  { symbol: 'SCHW', name: 'Charles Schwab Corporation', kind: 'stock' },
  { symbol: 'CHTR', name: 'Charter Communications', kind: 'stock' },
  { symbol: 'CVX', name: 'Chevron Corporation', kind: 'stock' },
  { symbol: 'CMG', name: 'Chipotle Mexican Grill', kind: 'stock' },
  { symbol: 'CB', name: 'Chubb Limited', kind: 'stock' },
  { symbol: 'CHD', name: 'Church & Dwight', kind: 'stock' },
  { symbol: 'CIEN', name: 'Ciena', kind: 'stock' },
  { symbol: 'CI', name: 'Cigna', kind: 'stock' },
  { symbol: 'CINF', name: 'Cincinnati Financial', kind: 'stock' },
  { symbol: 'CTAS', name: 'Cintas', kind: 'stock' },
  { symbol: 'CSCO', name: 'Cisco', kind: 'stock' },
  { symbol: 'C', name: 'Citigroup', kind: 'stock' },
  { symbol: 'CFG', name: 'Citizens Financial Group', kind: 'stock' },
  { symbol: 'CLX', name: 'Clorox', kind: 'stock' },
  { symbol: 'CME', name: 'CME Group', kind: 'stock' },
  { symbol: 'CMS', name: 'CMS Energy', kind: 'stock' },
  { symbol: 'KO', name: 'Coca-Cola', kind: 'stock' },
  { symbol: 'CTSH', name: 'Cognizant', kind: 'stock' },
  { symbol: 'COHR', name: 'Coherent Corp.', kind: 'stock' },
  { symbol: 'COIN', name: 'Coinbase', kind: 'stock' },
  { symbol: 'CL', name: 'Colgate-Palmolive', kind: 'stock' },
  { symbol: 'CMCSA', name: 'Comcast', kind: 'stock' },
  { symbol: 'FIX', name: 'Comfort Systems USA', kind: 'stock' },
  { symbol: 'CAG', name: 'Conagra Brands', kind: 'stock' },
  { symbol: 'COP', name: 'ConocoPhillips', kind: 'stock' },
  { symbol: 'ED', name: 'Consolidated Edison', kind: 'stock' },
  { symbol: 'STZ', name: 'Constellation Brands', kind: 'stock' },
  { symbol: 'CEG', name: 'Constellation Energy', kind: 'stock' },
  { symbol: 'COO', name: 'Cooper Companies', kind: 'stock' },
  { symbol: 'CPRT', name: 'Copart', kind: 'stock' },
  { symbol: 'GLW', name: 'Corning Inc.', kind: 'stock' },
  { symbol: 'CPAY', name: 'Corpay', kind: 'stock' },
  { symbol: 'CTVA', name: 'Corteva', kind: 'stock' },
  { symbol: 'CSGP', name: 'CoStar Group', kind: 'stock' },
  { symbol: 'COST', name: 'Costco', kind: 'stock' },
  { symbol: 'CTRA', name: 'Coterra', kind: 'stock' },
  { symbol: 'CRH', name: 'CRH plc', kind: 'stock' },
  { symbol: 'CRWD', name: 'CrowdStrike', kind: 'stock' },
  { symbol: 'CCI', name: 'Crown Castle', kind: 'stock' },
  { symbol: 'CSX', name: 'CSX Corporation', kind: 'stock' },
  { symbol: 'CMI', name: 'Cummins', kind: 'stock' },
  { symbol: 'CVS', name: 'CVS Health', kind: 'stock' },
  { symbol: 'DHR', name: 'Danaher Corporation', kind: 'stock' },
  { symbol: 'DRI', name: 'Darden Restaurants', kind: 'stock' },
  { symbol: 'DDOG', name: 'Datadog', kind: 'stock' },
  { symbol: 'DVA', name: 'DaVita', kind: 'stock' },
  { symbol: 'DECK', name: 'Deckers Brands', kind: 'stock' },
  { symbol: 'DE', name: 'Deere & Company', kind: 'stock' },
  { symbol: 'DELL', name: 'Dell Technologies', kind: 'stock' },
  { symbol: 'DAL', name: 'Delta Air Lines', kind: 'stock' },
  { symbol: 'DVN', name: 'Devon Energy', kind: 'stock' },
  { symbol: 'DXCM', name: 'Dexcom', kind: 'stock' },
  { symbol: 'FANG', name: 'Diamondback Energy', kind: 'stock' },
  { symbol: 'DLR', name: 'Digital Realty', kind: 'stock' },
  { symbol: 'DG', name: 'Dollar General', kind: 'stock' },
  { symbol: 'DLTR', name: 'Dollar Tree', kind: 'stock' },
  { symbol: 'D', name: 'Dominion Energy', kind: 'stock' },
  { symbol: 'DPZ', name: "Domino's", kind: 'stock' },
  { symbol: 'DASH', name: 'DoorDash', kind: 'stock' },
  { symbol: 'DOV', name: 'Dover Corporation', kind: 'stock' },
  { symbol: 'DOW', name: 'Dow Inc.', kind: 'stock' },
  { symbol: 'DHI', name: 'D. R. Horton', kind: 'stock' },
  { symbol: 'DTE', name: 'DTE Energy', kind: 'stock' },
  { symbol: 'DUK', name: 'Duke Energy', kind: 'stock' },
  { symbol: 'DD', name: 'DuPont', kind: 'stock' },
  { symbol: 'ETN', name: 'Eaton Corporation', kind: 'stock' },
  { symbol: 'EBAY', name: 'eBay Inc.', kind: 'stock' },
  { symbol: 'SATS', name: 'EchoStar', kind: 'stock' },
  { symbol: 'ECL', name: 'Ecolab', kind: 'stock' },
  { symbol: 'EIX', name: 'Edison International', kind: 'stock' },
  { symbol: 'EW', name: 'Edwards Lifesciences', kind: 'stock' },
  { symbol: 'EA', name: 'Electronic Arts', kind: 'stock' },
  { symbol: 'ELV', name: 'Elevance Health', kind: 'stock' },
  { symbol: 'EME', name: 'Emcor', kind: 'stock' },
  { symbol: 'EMR', name: 'Emerson Electric', kind: 'stock' },
  { symbol: 'ETR', name: 'Entergy', kind: 'stock' },
  { symbol: 'EOG', name: 'EOG Resources', kind: 'stock' },
  { symbol: 'EPAM', name: 'EPAM Systems', kind: 'stock' },
  { symbol: 'EQT', name: 'EQT Corporation', kind: 'stock' },
  { symbol: 'EFX', name: 'Equifax', kind: 'stock' },
  { symbol: 'EQIX', name: 'Equinix', kind: 'stock' },
  { symbol: 'EQR', name: 'Equity Residential', kind: 'stock' },
  { symbol: 'ERIE', name: 'Erie Indemnity', kind: 'stock' },
  { symbol: 'ESS', name: 'Essex Property Trust', kind: 'stock' },
  { symbol: 'EL', name: 'Estee Lauder Companies', kind: 'stock' },
  { symbol: 'EG', name: 'Everest Group', kind: 'stock' },
  { symbol: 'EVRG', name: 'Evergy', kind: 'stock' },
  { symbol: 'ES', name: 'Eversource Energy', kind: 'stock' },
  { symbol: 'EXC', name: 'Exelon', kind: 'stock' },
  { symbol: 'EXE', name: 'Expand Energy', kind: 'stock' },
  { symbol: 'EXPE', name: 'Expedia Group', kind: 'stock' },
  { symbol: 'EXPD', name: 'Expeditors International', kind: 'stock' },
  { symbol: 'EXR', name: 'Extra Space Storage', kind: 'stock' },
  { symbol: 'XOM', name: 'ExxonMobil', kind: 'stock' },
  { symbol: 'FFIV', name: 'F5, Inc.', kind: 'stock' },
  { symbol: 'FDS', name: 'FactSet', kind: 'stock' },
  { symbol: 'FICO', name: 'Fair Isaac', kind: 'stock' },
  { symbol: 'FAST', name: 'Fastenal', kind: 'stock' },
  { symbol: 'FRT', name: 'Federal Realty Investment Trust', kind: 'stock' },
  { symbol: 'FDX', name: 'FedEx', kind: 'stock' },
  { symbol: 'FIS', name: 'Fidelity National Information Services', kind: 'stock' },
  { symbol: 'FITB', name: 'Fifth Third Bancorp', kind: 'stock' },
  { symbol: 'FSLR', name: 'First Solar', kind: 'stock' },
  { symbol: 'FE', name: 'FirstEnergy', kind: 'stock' },
  { symbol: 'FISV', name: 'Fiserv', kind: 'stock' },
  { symbol: 'F', name: 'Ford Motor Company', kind: 'stock' },
  { symbol: 'FTNT', name: 'Fortinet', kind: 'stock' },
  { symbol: 'FTV', name: 'Fortive', kind: 'stock' },
  { symbol: 'FOXA', name: 'Fox Corporation (Class A)', kind: 'stock' },
  { symbol: 'FOX', name: 'Fox Corporation (Class B)', kind: 'stock' },
  { symbol: 'BEN', name: 'Franklin Resources', kind: 'stock' },
  { symbol: 'FCX', name: 'Freeport-McMoRan', kind: 'stock' },
  { symbol: 'GRMN', name: 'Garmin', kind: 'stock' },
  { symbol: 'IT', name: 'Gartner', kind: 'stock' },
  { symbol: 'GE', name: 'GE Aerospace', kind: 'stock' },
  { symbol: 'GEHC', name: 'GE HealthCare', kind: 'stock' },
  { symbol: 'GEV', name: 'GE Vernova', kind: 'stock' },
  { symbol: 'GEN', name: 'Gen Digital', kind: 'stock' },
  { symbol: 'GNRC', name: 'Generac', kind: 'stock' },
  { symbol: 'GD', name: 'General Dynamics', kind: 'stock' },
  { symbol: 'GIS', name: 'General Mills', kind: 'stock' },
  { symbol: 'GM', name: 'General Motors', kind: 'stock' },
  { symbol: 'GPC', name: 'Genuine Parts Company', kind: 'stock' },
  { symbol: 'GILD', name: 'Gilead Sciences', kind: 'stock' },
  { symbol: 'GPN', name: 'Global Payments', kind: 'stock' },
  { symbol: 'GL', name: 'Globe Life', kind: 'stock' },
  { symbol: 'GDDY', name: 'GoDaddy', kind: 'stock' },
  { symbol: 'GS', name: 'Goldman Sachs', kind: 'stock' },
  { symbol: 'HAL', name: 'Halliburton', kind: 'stock' },
  { symbol: 'HIG', name: 'Hartford', kind: 'stock' },
  { symbol: 'HAS', name: 'Hasbro', kind: 'stock' },
  { symbol: 'HCA', name: 'HCA Healthcare', kind: 'stock' },
  { symbol: 'DOC', name: 'Healthpeak Properties', kind: 'stock' },
  { symbol: 'HSIC', name: 'Henry Schein', kind: 'stock' },
  { symbol: 'HSY', name: 'Hershey Company', kind: 'stock' },
  { symbol: 'HPE', name: 'Hewlett Packard Enterprise', kind: 'stock' },
  { symbol: 'HLT', name: 'Hilton Worldwide', kind: 'stock' },
  { symbol: 'HD', name: 'Home Depot', kind: 'stock' },
  { symbol: 'HON', name: 'Honeywell', kind: 'stock' },
  { symbol: 'HRL', name: 'Hormel Foods', kind: 'stock' },
  { symbol: 'HST', name: 'Host Hotels & Resorts', kind: 'stock' },
  { symbol: 'HWM', name: 'Howmet Aerospace', kind: 'stock' },
  { symbol: 'HPQ', name: 'HP Inc.', kind: 'stock' },
  { symbol: 'HUBB', name: 'Hubbell Incorporated', kind: 'stock' },
  { symbol: 'HUM', name: 'Humana', kind: 'stock' },
  { symbol: 'HBAN', name: 'Huntington Bancshares', kind: 'stock' },
  { symbol: 'HII', name: 'Huntington Ingalls Industries', kind: 'stock' },
  { symbol: 'IBM', name: 'IBM', kind: 'stock' },
  { symbol: 'IEX', name: 'IDEX Corporation', kind: 'stock' },
  { symbol: 'IDXX', name: 'Idexx Laboratories', kind: 'stock' },
  { symbol: 'ITW', name: 'Illinois Tool Works', kind: 'stock' },
  { symbol: 'INCY', name: 'Incyte', kind: 'stock' },
  { symbol: 'IR', name: 'Ingersoll Rand', kind: 'stock' },
  { symbol: 'PODD', name: 'Insulet Corporation', kind: 'stock' },
  { symbol: 'INTC', name: 'Intel', kind: 'stock' },
  { symbol: 'IBKR', name: 'Interactive Brokers', kind: 'stock' },
  { symbol: 'ICE', name: 'Intercontinental Exchange', kind: 'stock' },
  { symbol: 'IFF', name: 'International Flavors & Fragrances', kind: 'stock' },
  { symbol: 'IP', name: 'International Paper', kind: 'stock' },
  { symbol: 'INTU', name: 'Intuit', kind: 'stock' },
  { symbol: 'ISRG', name: 'Intuitive Surgical', kind: 'stock' },
  { symbol: 'IVZ', name: 'Invesco', kind: 'stock' },
  { symbol: 'INVH', name: 'Invitation Homes', kind: 'stock' },
  { symbol: 'IQV', name: 'IQVIA', kind: 'stock' },
  { symbol: 'IRM', name: 'Iron Mountain', kind: 'stock' },
  { symbol: 'JBHT', name: 'J.B. Hunt', kind: 'stock' },
  { symbol: 'JBL', name: 'Jabil', kind: 'stock' },
  { symbol: 'JKHY', name: 'Jack Henry & Associates', kind: 'stock' },
  { symbol: 'J', name: 'Jacobs Solutions', kind: 'stock' },
  { symbol: 'JNJ', name: 'Johnson & Johnson', kind: 'stock' },
  { symbol: 'JCI', name: 'Johnson Controls', kind: 'stock' },
  { symbol: 'JPM', name: 'JPMorgan Chase', kind: 'stock' },
  { symbol: 'KVUE', name: 'Kenvue', kind: 'stock' },
  { symbol: 'KDP', name: 'Keurig Dr Pepper', kind: 'stock' },
  { symbol: 'KEY', name: 'KeyCorp', kind: 'stock' },
  { symbol: 'KEYS', name: 'Keysight Technologies', kind: 'stock' },
  { symbol: 'KMB', name: 'Kimberly-Clark', kind: 'stock' },
  { symbol: 'KIM', name: 'Kimco Realty', kind: 'stock' },
  { symbol: 'KMI', name: 'Kinder Morgan', kind: 'stock' },
  { symbol: 'KKR', name: 'KKR & Co.', kind: 'stock' },
  { symbol: 'KLAC', name: 'KLA Corporation', kind: 'stock' },
  { symbol: 'KHC', name: 'Kraft Heinz', kind: 'stock' },
  { symbol: 'KR', name: 'Kroger', kind: 'stock' },
  { symbol: 'LHX', name: 'L3Harris', kind: 'stock' },
  { symbol: 'LH', name: 'Labcorp', kind: 'stock' },
  { symbol: 'LRCX', name: 'Lam Research', kind: 'stock' },
  { symbol: 'LVS', name: 'Las Vegas Sands', kind: 'stock' },
  { symbol: 'LDOS', name: 'Leidos', kind: 'stock' },
  { symbol: 'LEN', name: 'Lennar', kind: 'stock' },
  { symbol: 'LII', name: 'Lennox International', kind: 'stock' },
  { symbol: 'LLY', name: 'Eli Lilly', kind: 'stock' },
  { symbol: 'LIN', name: 'Linde plc', kind: 'stock' },
  { symbol: 'LYV', name: 'Live Nation Entertainment', kind: 'stock' },
  { symbol: 'LMT', name: 'Lockheed Martin', kind: 'stock' },
  { symbol: 'L', name: 'Loews Corporation', kind: 'stock' },
  { symbol: 'LOW', name: "Lowe's", kind: 'stock' },
  { symbol: 'LULU', name: 'Lululemon Athletica', kind: 'stock' },
  { symbol: 'LITE', name: 'Lumentum', kind: 'stock' },
  { symbol: 'LYB', name: 'LyondellBasell', kind: 'stock' },
  { symbol: 'MTB', name: 'M&T Bank', kind: 'stock' },
  { symbol: 'MPC', name: 'Marathon Petroleum', kind: 'stock' },
  { symbol: 'MAR', name: 'Marriott International', kind: 'stock' },
  { symbol: 'MRSH', name: 'Marsh McLennan', kind: 'stock' },
  { symbol: 'MLM', name: 'Martin Marietta Materials', kind: 'stock' },
  { symbol: 'MAS', name: 'Masco', kind: 'stock' },
  { symbol: 'MA', name: 'Mastercard', kind: 'stock' },
  { symbol: 'MKC', name: 'McCormick & Company', kind: 'stock' },
  { symbol: 'MCD', name: "McDonald's", kind: 'stock' },
  { symbol: 'MCK', name: 'McKesson Corporation', kind: 'stock' },
  { symbol: 'MDT', name: 'Medtronic', kind: 'stock' },
  { symbol: 'MRK', name: 'Merck & Co.', kind: 'stock' },
  { symbol: 'META', name: 'Meta Platforms', kind: 'stock' },
  { symbol: 'MET', name: 'MetLife', kind: 'stock' },
  { symbol: 'MTD', name: 'Mettler Toledo', kind: 'stock' },
  { symbol: 'MGM', name: 'MGM Resorts', kind: 'stock' },
  { symbol: 'MCHP', name: 'Microchip Technology', kind: 'stock' },
  { symbol: 'MU', name: 'Micron Technology', kind: 'stock' },
  { symbol: 'MSFT', name: 'Microsoft', kind: 'stock' },
  { symbol: 'MAA', name: 'Mid-America Apartment Communities', kind: 'stock' },
  { symbol: 'MRNA', name: 'Moderna', kind: 'stock' },
  { symbol: 'TAP', name: 'Molson Coors Beverage Company', kind: 'stock' },
  { symbol: 'MDLZ', name: 'Mondelez International', kind: 'stock' },
  { symbol: 'MPWR', name: 'Monolithic Power Systems', kind: 'stock' },
  { symbol: 'MNST', name: 'Monster Beverage', kind: 'stock' },
  { symbol: 'MCO', name: "Moody's Corporation", kind: 'stock' },
  { symbol: 'MS', name: 'Morgan Stanley', kind: 'stock' },
  { symbol: 'MOS', name: 'Mosaic Company', kind: 'stock' },
  { symbol: 'MSI', name: 'Motorola Solutions', kind: 'stock' },
  { symbol: 'MSCI', name: 'MSCI Inc.', kind: 'stock' },
  { symbol: 'NDAQ', name: 'Nasdaq, Inc.', kind: 'stock' },
  { symbol: 'NTAP', name: 'NetApp', kind: 'stock' },
  { symbol: 'NFLX', name: 'Netflix', kind: 'stock' },
  { symbol: 'NEM', name: 'Newmont', kind: 'stock' },
  { symbol: 'NWSA', name: 'News Corp (Class A)', kind: 'stock' },
  { symbol: 'NWS', name: 'News Corp (Class B)', kind: 'stock' },
  { symbol: 'NEE', name: 'NextEra Energy', kind: 'stock' },
  { symbol: 'NKE', name: 'Nike', kind: 'stock' },
  { symbol: 'NI', name: 'NiSource', kind: 'stock' },
  { symbol: 'NDSN', name: 'Nordson Corporation', kind: 'stock' },
  { symbol: 'NSC', name: 'Norfolk Southern', kind: 'stock' },
  { symbol: 'NTRS', name: 'Northern Trust', kind: 'stock' },
  { symbol: 'NOC', name: 'Northrop Grumman', kind: 'stock' },
  { symbol: 'NCLH', name: 'Norwegian Cruise Line Holdings', kind: 'stock' },
  { symbol: 'NRG', name: 'NRG Energy', kind: 'stock' },
  { symbol: 'NUE', name: 'Nucor', kind: 'stock' },
  { symbol: 'NVDA', name: 'Nvidia', kind: 'stock' },
  { symbol: 'NVR', name: 'NVR Inc.', kind: 'stock' },
  { symbol: 'NXPI', name: 'NXP Semiconductors', kind: 'stock' },
  { symbol: 'ORLY', name: "O'Reilly Automotive", kind: 'stock' },
  { symbol: 'OXY', name: 'Occidental Petroleum', kind: 'stock' },
  { symbol: 'ODFL', name: 'Old Dominion', kind: 'stock' },
  { symbol: 'OMC', name: 'Omnicom Group', kind: 'stock' },
  { symbol: 'ON', name: 'ON Semiconductor', kind: 'stock' },
  { symbol: 'OKE', name: 'Oneok', kind: 'stock' },
  { symbol: 'ORCL', name: 'Oracle Corporation', kind: 'stock' },
  { symbol: 'OTIS', name: 'Otis Worldwide', kind: 'stock' },
  { symbol: 'PCAR', name: 'Paccar', kind: 'stock' },
  { symbol: 'PKG', name: 'Packaging Corporation of America', kind: 'stock' },
  { symbol: 'PLTR', name: 'Palantir Technologies', kind: 'stock' },
  { symbol: 'PANW', name: 'Palo Alto Networks', kind: 'stock' },
  { symbol: 'PSKY', name: 'Paramount Skydance Corporation', kind: 'stock' },
  { symbol: 'PH', name: 'Parker Hannifin', kind: 'stock' },
  { symbol: 'PAYX', name: 'Paychex', kind: 'stock' },
  { symbol: 'PYPL', name: 'PayPal', kind: 'stock' },
  { symbol: 'PNR', name: 'Pentair', kind: 'stock' },
  { symbol: 'PEP', name: 'PepsiCo', kind: 'stock' },
  { symbol: 'PFE', name: 'Pfizer', kind: 'stock' },
  { symbol: 'PCG', name: 'PG&E Corporation', kind: 'stock' },
  { symbol: 'PM', name: 'Philip Morris International', kind: 'stock' },
  { symbol: 'PSX', name: 'Phillips 66', kind: 'stock' },
  { symbol: 'PNW', name: 'Pinnacle West Capital', kind: 'stock' },
  { symbol: 'PNC', name: 'PNC Financial Services', kind: 'stock' },
  { symbol: 'POOL', name: 'Pool Corporation', kind: 'stock' },
  { symbol: 'PPG', name: 'PPG Industries', kind: 'stock' },
  { symbol: 'PPL', name: 'PPL Corporation', kind: 'stock' },
  { symbol: 'PFG', name: 'Principal Financial Group', kind: 'stock' },
  { symbol: 'PG', name: 'Procter & Gamble', kind: 'stock' },
  { symbol: 'PGR', name: 'Progressive Corporation', kind: 'stock' },
  { symbol: 'PLD', name: 'Prologis', kind: 'stock' },
  { symbol: 'PRU', name: 'Prudential Financial', kind: 'stock' },
  { symbol: 'PEG', name: 'Public Service Enterprise Group', kind: 'stock' },
  { symbol: 'PTC', name: 'PTC Inc.', kind: 'stock' },
  { symbol: 'PSA', name: 'Public Storage', kind: 'stock' },
  { symbol: 'PHM', name: 'PulteGroup', kind: 'stock' },
  { symbol: 'PWR', name: 'Quanta Services', kind: 'stock' },
  { symbol: 'QCOM', name: 'Qualcomm', kind: 'stock' },
  { symbol: 'DGX', name: 'Quest Diagnostics', kind: 'stock' },
  { symbol: 'Q', name: 'Qnity Electronics', kind: 'stock' },
  { symbol: 'RL', name: 'Ralph Lauren Corporation', kind: 'stock' },
  { symbol: 'RJF', name: 'Raymond James Financial', kind: 'stock' },
  { symbol: 'RTX', name: 'RTX Corporation', kind: 'stock' },
  { symbol: 'O', name: 'Realty Income', kind: 'stock' },
  { symbol: 'REG', name: 'Regency Centers', kind: 'stock' },
  { symbol: 'REGN', name: 'Regeneron Pharmaceuticals', kind: 'stock' },
  { symbol: 'RF', name: 'Regions Financial Corporation', kind: 'stock' },
  { symbol: 'RSG', name: 'Republic Services', kind: 'stock' },
  { symbol: 'RMD', name: 'ResMed', kind: 'stock' },
  { symbol: 'RVTY', name: 'Revvity', kind: 'stock' },
  { symbol: 'HOOD', name: 'Robinhood Markets', kind: 'stock' },
  { symbol: 'ROK', name: 'Rockwell Automation', kind: 'stock' },
  { symbol: 'ROL', name: 'Rollins Inc.', kind: 'stock' },
  { symbol: 'ROP', name: 'Roper Technologies', kind: 'stock' },
  { symbol: 'ROST', name: 'Ross Stores', kind: 'stock' },
  { symbol: 'RCL', name: 'Royal Caribbean Group', kind: 'stock' },
  { symbol: 'SPGI', name: 'S&P Global', kind: 'stock' },
  { symbol: 'CRM', name: 'Salesforce', kind: 'stock' },
  { symbol: 'SNDK', name: 'Sandisk', kind: 'stock' },
  { symbol: 'SBAC', name: 'SBA Communications', kind: 'stock' },
  { symbol: 'SLB', name: 'Schlumberger', kind: 'stock' },
  { symbol: 'STX', name: 'Seagate Technology', kind: 'stock' },
  { symbol: 'SRE', name: 'Sempra', kind: 'stock' },
  { symbol: 'NOW', name: 'ServiceNow', kind: 'stock' },
  { symbol: 'SHW', name: 'Sherwin-Williams', kind: 'stock' },
  { symbol: 'SPG', name: 'Simon Property Group', kind: 'stock' },
  { symbol: 'SWKS', name: 'Skyworks Solutions', kind: 'stock' },
  { symbol: 'SJM', name: 'J.M. Smucker Company', kind: 'stock' },
  { symbol: 'SW', name: 'Smurfit Westrock', kind: 'stock' },
  { symbol: 'SNA', name: 'Snap-on', kind: 'stock' },
  { symbol: 'SOLV', name: 'Solventum', kind: 'stock' },
  { symbol: 'SO', name: 'Southern Company', kind: 'stock' },
  { symbol: 'LUV', name: 'Southwest Airlines', kind: 'stock' },
  { symbol: 'SWK', name: 'Stanley Black & Decker', kind: 'stock' },
  { symbol: 'SBUX', name: 'Starbucks', kind: 'stock' },
  { symbol: 'STT', name: 'State Street Corporation', kind: 'stock' },
  { symbol: 'STLD', name: 'Steel Dynamics', kind: 'stock' },
  { symbol: 'STE', name: 'Steris', kind: 'stock' },
  { symbol: 'SYK', name: 'Stryker Corporation', kind: 'stock' },
  { symbol: 'SMCI', name: 'Supermicro', kind: 'stock' },
  { symbol: 'SYF', name: 'Synchrony Financial', kind: 'stock' },
  { symbol: 'SNPS', name: 'Synopsys', kind: 'stock' },
  { symbol: 'SYY', name: 'Sysco', kind: 'stock' },
  { symbol: 'TMUS', name: 'T-Mobile US', kind: 'stock' },
  { symbol: 'TROW', name: 'T. Rowe Price', kind: 'stock' },
  { symbol: 'TTWO', name: 'Take-Two Interactive', kind: 'stock' },
  { symbol: 'TPR', name: 'Tapestry, Inc.', kind: 'stock' },
  { symbol: 'TRGP', name: 'Targa Resources', kind: 'stock' },
  { symbol: 'TGT', name: 'Target Corporation', kind: 'stock' },
  { symbol: 'TEL', name: 'TE Connectivity', kind: 'stock' },
  { symbol: 'TDY', name: 'Teledyne Technologies', kind: 'stock' },
  { symbol: 'TER', name: 'Teradyne', kind: 'stock' },
  { symbol: 'TSLA', name: 'Tesla, Inc.', kind: 'stock' },
  { symbol: 'TXN', name: 'Texas Instruments', kind: 'stock' },
  { symbol: 'TPL', name: 'Texas Pacific Land Corporation', kind: 'stock' },
  { symbol: 'TXT', name: 'Textron', kind: 'stock' },
  { symbol: 'TMO', name: 'Thermo Fisher Scientific', kind: 'stock' },
  { symbol: 'TJX', name: 'TJX Companies', kind: 'stock' },
  { symbol: 'TKO', name: 'TKO Group Holdings', kind: 'stock' },
  { symbol: 'TTD', name: 'Trade Desk', kind: 'stock' },
  { symbol: 'TSCO', name: 'Tractor Supply', kind: 'stock' },
  { symbol: 'TT', name: 'Trane Technologies', kind: 'stock' },
  { symbol: 'TDG', name: 'TransDigm Group', kind: 'stock' },
  { symbol: 'TRV', name: 'Travelers Companies', kind: 'stock' },
  { symbol: 'TRMB', name: 'Trimble Inc.', kind: 'stock' },
  { symbol: 'TFC', name: 'Truist Financial', kind: 'stock' },
  { symbol: 'TYL', name: 'Tyler Technologies', kind: 'stock' },
  { symbol: 'TSN', name: 'Tyson Foods', kind: 'stock' },
  { symbol: 'USB', name: 'U.S. Bancorp', kind: 'stock' },
  { symbol: 'UBER', name: 'Uber', kind: 'stock' },
  { symbol: 'UDR', name: 'UDR Inc.', kind: 'stock' },
  { symbol: 'ULTA', name: 'Ulta Beauty', kind: 'stock' },
  { symbol: 'UNP', name: 'Union Pacific Corporation', kind: 'stock' },
  { symbol: 'UAL', name: 'United Airlines Holdings', kind: 'stock' },
  { symbol: 'UPS', name: 'United Parcel Service', kind: 'stock' },
  { symbol: 'URI', name: 'United Rentals', kind: 'stock' },
  { symbol: 'UNH', name: 'UnitedHealth Group', kind: 'stock' },
  { symbol: 'UHS', name: 'Universal Health Services', kind: 'stock' },
  { symbol: 'VLO', name: 'Valero Energy', kind: 'stock' },
  { symbol: 'VTR', name: 'Ventas', kind: 'stock' },
  { symbol: 'VLTO', name: 'Veralto', kind: 'stock' },
  { symbol: 'VRSN', name: 'Verisign', kind: 'stock' },
  { symbol: 'VRSK', name: 'Verisk Analytics', kind: 'stock' },
  { symbol: 'VZ', name: 'Verizon', kind: 'stock' },
  { symbol: 'VRTX', name: 'Vertex Pharmaceuticals', kind: 'stock' },
  { symbol: 'VRT', name: 'Vertiv', kind: 'stock' },
  { symbol: 'VTRS', name: 'Viatris', kind: 'stock' },
  { symbol: 'VICI', name: 'Vici Properties', kind: 'stock' },
  { symbol: 'V', name: 'Visa Inc.', kind: 'stock' },
  { symbol: 'VST', name: 'Vistra Corp.', kind: 'stock' },
  { symbol: 'VMC', name: 'Vulcan Materials Company', kind: 'stock' },
  { symbol: 'WRB', name: 'W. R. Berkley Corporation', kind: 'stock' },
  { symbol: 'GWW', name: 'W. W. Grainger', kind: 'stock' },
  { symbol: 'WAB', name: 'Wabtec', kind: 'stock' },
  { symbol: 'WMT', name: 'Walmart', kind: 'stock' },
  { symbol: 'DIS', name: 'Walt Disney Company', kind: 'stock' },
  { symbol: 'WBD', name: 'Warner Bros. Discovery', kind: 'stock' },
  { symbol: 'WM', name: 'Waste Management', kind: 'stock' },
  { symbol: 'WAT', name: 'Waters Corporation', kind: 'stock' },
  { symbol: 'WEC', name: 'WEC Energy Group', kind: 'stock' },
  { symbol: 'WFC', name: 'Wells Fargo', kind: 'stock' },
  { symbol: 'WELL', name: 'Welltower', kind: 'stock' },
  { symbol: 'WST', name: 'West Pharmaceutical Services', kind: 'stock' },
  { symbol: 'WDC', name: 'Western Digital', kind: 'stock' },
  { symbol: 'WY', name: 'Weyerhaeuser', kind: 'stock' },
  { symbol: 'WSM', name: 'Williams-Sonoma, Inc.', kind: 'stock' },
  { symbol: 'WMB', name: 'Williams Companies', kind: 'stock' },
  { symbol: 'WTW', name: 'Willis Towers Watson', kind: 'stock' },
  { symbol: 'WDAY', name: 'Workday, Inc.', kind: 'stock' },
  { symbol: 'WYNN', name: 'Wynn Resorts', kind: 'stock' },
  { symbol: 'XEL', name: 'Xcel Energy', kind: 'stock' },
  { symbol: 'XYL', name: 'Xylem Inc.', kind: 'stock' },
  { symbol: 'YUM', name: 'Yum! Brands', kind: 'stock' },
  { symbol: 'ZBRA', name: 'Zebra Technologies', kind: 'stock' },
  { symbol: 'ZBH', name: 'Zimmer Biomet', kind: 'stock' },
  { symbol: 'ZTS', name: 'Zoetis', kind: 'stock' },
];

/**
 * Filter the bundled list by free-text query. Matches symbol or name
 * (case-insensitive substring). Caller decides display window (we cap
 * at 50 results to keep render cheap; user typing more refines further).
 */
export function searchBundledTickers(query: string, kindFilter?: SecurityAssetClass[]): BundledTicker[] {
  const q = query.trim().toLowerCase();
  const filtered = kindFilter && kindFilter.length > 0
    ? BUNDLED_TICKERS.filter(t => kindFilter.includes(t.kind))
    : BUNDLED_TICKERS;
  if (q === '') return filtered.slice(0, 50);
  const matches: BundledTicker[] = [];
  for (const t of filtered) {
    if (t.symbol.toLowerCase().includes(q) || t.name.toLowerCase().includes(q)) {
      matches.push(t);
      if (matches.length >= 50) break;
    }
  }
  return matches;
}
